import { useState, useCallback } from 'react'
import { Upload, FileText, X } from 'lucide-react'

interface FileDropZoneProps {
  onFilesDropped: (files: File[]) => void
  files: File[]
  isProcessing?: boolean
}

export default function FileDropZone({ onFilesDropped, files, isProcessing = false }: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  // Merge new files with existing, replacing by type (DWG/DXF vs PDF).
  // STEP is disabled for now — a DWG or DXF drawing is required.
  const mergeFiles = useCallback((incoming: File[]) => {
    const merged = [...files]
    for (const f of incoming) {
      const ext = f.name.split('.').pop()?.toLowerCase()
      if (ext === 'step' || ext === 'stp') continue // STEP not accepted
      const category = ext === 'pdf' ? 'pdf' : 'dwg' // dwg/dxf
      // Replace existing file of the same category, or append
      const idx = merged.findIndex(existing => {
        const existExt = existing.name.split('.').pop()?.toLowerCase()
        if (category === 'pdf') return existExt === 'pdf'
        return existExt === 'dwg' || existExt === 'dxf'
      })
      if (idx >= 0) {
        merged[idx] = f
      } else {
        merged.push(f)
      }
    }
    onFilesDropped(merged)
  }, [files, onFilesDropped])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length > 0) {
      mergeFiles(dropped)
    }
  }, [mergeFiles])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length > 0) {
      mergeFiles(selected)
    }
    // Reset so the same file can be re-selected
    e.target.value = ''
  }, [mergeFiles])

  const handleRemove = useCallback((index: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const remaining = files.filter((_, i) => i !== index)
    // Signal removal by calling with empty — parent tracks state
    onFilesDropped(remaining.length > 0 ? remaining : [])
  }, [files, onFilesDropped])

  const hasFiles = files.length > 0

  return (
    <label
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative flex flex-col items-center justify-center gap-2
        w-full rounded-lg cursor-pointer
        transition-all duration-200
        ${isProcessing ? 'border-0' : 'border-2 border-dashed'}
        ${hasFiles ? 'py-3 px-3' : 'h-32'}
        ${isDragging
          ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
          : isProcessing
            ? 'bg-white/[0.02]'
            : 'border-cyan-500/30 bg-white/[0.02] hover:border-cyan-500/50 hover:bg-white/[0.04]'
        }
      `}
    >
      {/* Animated border sweep — visible only when processing */}
      {isProcessing && <BorderSweep />}

      <input
        type="file"
        accept=".dwg,.dxf,.DWG,.DXF,.pdf,.PDF"
        multiple
        className="sr-only"
        onChange={handleInputChange}
      />
      {hasFiles ? (
        <div className="w-full space-y-1.5">
          {files.map((file, i) => {
            const ext = file.name.split('.').pop()?.toUpperCase() ?? ''
            const isPdf = ext === 'PDF'
            const isStep = ext === 'STEP' || ext === 'STP'
            const iconColor = isPdf ? 'text-amber-400' : isStep ? 'text-emerald-400' : 'text-cyan-400'
            return (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-2 rounded-md bg-white/[0.03] border border-cyan-500/15 px-2.5 py-1.5"
              >
                <FileText className={`w-4 h-4 shrink-0 ${iconColor}`} />
                <span className="font-mono text-xs text-slate-300 truncate flex-1">
                  {file.name}
                </span>
                <span className="font-mono text-[10px] text-slate-600 shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={(e) => handleRemove(i, e)}
                  className="shrink-0 p-0.5 rounded text-slate-600 hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )
          })}
          <div className="text-center pt-0.5">
            <span className="font-mono text-[10px] text-slate-600">
              Drop more files or click to add
            </span>
          </div>
        </div>
      ) : (
        <>
          <Upload
            className={`w-6 h-6 transition-colors ${isDragging ? 'text-cyan-400' : 'text-slate-500'}`}
          />
          <span className={`font-mono text-sm transition-colors ${isDragging ? 'text-cyan-300' : 'text-slate-400'}`}>
            Drop files here — DWG or DXF (PDF optional)
          </span>
          <span className="font-mono text-xs text-slate-600">
            or click to browse
          </span>
        </>
      )}
    </label>
  )
}

/**
 * Animated border overlay: a gradient beam sweeps top→bottom along the
 * border outline only (interior is masked out). The beam is brightest at
 * the leading edge and fades to transparent, creating the "reducing
 * brightness" effect the user requested.
 */
function BorderSweep() {
  return (
    <>
      <style>{`
        @keyframes borderSweepDown {
          0%   { transform: translateY(-60%); }
          100% { transform: translateY(60%); }
        }
      `}</style>
      {/* Mask container: clips the inner gradient to only show a 2px border strip */}
      <div
        className="absolute inset-0 rounded-lg pointer-events-none overflow-hidden"
        style={{
          padding: '2px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        }}
      >
        {/* Sweeping gradient beam */}
        <div
          className="absolute inset-x-0"
          style={{
            height: '250%',
            background: `linear-gradient(
              to bottom,
              rgba(6, 182, 212, 0.8) 0%,
              rgba(6, 182, 212, 0.5) 5%,
              rgba(6, 182, 212, 0.2) 15%,
              rgba(6, 182, 212, 0.05) 30%,
              transparent 45%,
              transparent 100%
            )`,
            animation: 'borderSweepDown 2.5s ease-in-out infinite',
          }}
        />
      </div>
      {/* Faint static border so the shape is always visible */}
      <div className="absolute inset-0 rounded-lg border border-cyan-500/20 pointer-events-none" />
    </>
  )
}
